import type { Child, PickupRequest } from "@spork/s4p-server-sdk";
import { Link, type Href } from "expo-router";
import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Pressable, View } from "react-native";

import { Badge } from "@/components/ui/badge";
import { Text } from "@/components/ui/text";
import { getChildLabel } from "@/features/selectedChild";
import { formatDisplayDateWithWeekday, formatIsoDate } from "@/lib/date";
import { TEST_IDS } from "@/testIdDefinition";
import { LeaveIcon, PickupIcon } from "../../components/SectionItemIcon";
import { Icon } from "../../components/ui/icon";
import { getPickupsOnNearestDate } from "../pickupRequest/pickupRequestSchedule";
import { getLeavesAloneSummary } from "./leavesAlone";

type ChildAbsenceSummarySectionProps = {
    childList: Child[];
    childIdsNeedingInfo: ReadonlySet<number>;
    pickupRequests: PickupRequest[];
};

type StudentActionRowProps = {
    href: Href;
    label: string;
    badge?: string;
    accessibilityLabel: string;
    testID: string;
    children?: ReactNode;
    icon?: ReactNode;
    subtitle?: ReactNode;
};

function StudentActionRow({
    href,
    label,
    badge,
    accessibilityLabel,
    icon,
    testID,
    children,
    subtitle,
}: StudentActionRowProps) {
    return (
        <Link href={href} asChild>
            <Pressable
                testID={testID}
                className="flex-column px-4 py-4 active:bg-accent rounded-lg bg-card border border-border shadow-sm shadow-black/5"
                accessibilityRole="button"
                accessibilityLabel={accessibilityLabel}
            >
                <View className="flex-row justify-between gap-2">
                    <View className="min-w-0 flex-1">
                        <View className="flex-row flex-wrap items-center gap-2">
                            <Text>{label}</Text>
                            {badge ? (
                                <Badge variant="secondary">
                                    <Text
                                        testID={
                                            TEST_IDS[
                                                TEST_IDS.HOME_STUDENT_ROW_BADGE
                                            ]
                                        }
                                    >
                                        {badge.replaceAll(" ", "\u00A0")}
                                    </Text>
                                </Badge>
                            ) : null}
                        </View>
                        {subtitle}
                    </View>
                    <View className="flex-row items-center self-start gap-2">
                        <View className="shrink-0">{icon}</View>
                        <Icon name="chevron-right" size={14} />
                    </View>
                </View>
                {children}
            </Pressable>
        </Link>
    );
}

type ChildSummaryCardProps = {
    child: Child;
    needsInfo: boolean;
    children?: ReactNode;
};

function ChildSummaryCard({
    child,
    needsInfo,
    children,
}: ChildSummaryCardProps) {
    const { t } = useTranslation();
    const childLabel = getChildLabel(child);
    const leavesAlone = getLeavesAloneSummary(
        child.leaves_alone,
        (isoWeekday) => t(`ui.weekday.${isoWeekday}`),
    );
    const standingRule =
        child.leaves_alone == null
            ? undefined
            : leavesAlone
              ? t("home.walksHome", { days: leavesAlone })
              : t("home.pickupRequiredAlways");

    return (
        <View className="mb-2 mt-2 gap-2">
            <View className="flex-row items-center gap-3">
                <Icon
                    name="user"
                    iconStyle="solid"
                    size={14}
                    className="text-primary"
                />
                <Text testID={TEST_IDS[TEST_IDS.HOME_STUDENT_ROW_NAME]}>
                    {childLabel}
                </Text>
            </View>

            <StudentActionRow
                href={`/child/${child.id}/absences` as Href}
                label={t("home.leaveRequests")}
                badge={needsInfo ? t("home.leaveNeedsAction") : undefined}
                accessibilityLabel={
                    needsInfo
                        ? t("home.viewAbsencesNeedsInfoA11y", {
                              name: childLabel,
                          })
                        : t("home.viewAbsencesA11y", { name: childLabel })
                }
                testID={TEST_IDS[TEST_IDS.HOME_STUDENT_ROW]}
                icon={<LeaveIcon size="md" />}
                subtitle={
                    <Text
                        variant="muted"
                        testID={TEST_IDS[TEST_IDS.HOME_STUDENT_ROW_SUBTITLE]}
                    >
                        {t("home.viewAbsences")}
                    </Text>
                }
            />

            <StudentActionRow
                href={`/child/${child.id}/pickups` as Href}
                label={t("home.pickupRequests")}
                accessibilityLabel={t("home.viewPickupA11y", {
                    name: childLabel,
                })}
                testID={TEST_IDS[TEST_IDS.HOME_STUDENT_PICKUP_ROW]}
                icon={<PickupIcon size="md" />}
                subtitle={
                    standingRule ? (
                        <Text
                            variant="muted"
                            testID={
                                TEST_IDS[TEST_IDS.HOME_STUDENT_ROW_LEAVES_ALONE]
                            }
                        >
                            {standingRule}
                        </Text>
                    ) : null
                }
            >
                {children}
            </StudentActionRow>
        </View>
    );
}

function PickupChangeCard({
    nextPickup,
}: {
    nextPickup?: { date: string; picker: string };
}) {
    const { t } = useTranslation();
    return (
        <View className="flex-column gap-1 mt-4">
            <View className="flex-row items-center justify-between gap-2">
                <Text variant="label">{t("home.pickupChange")}</Text>
            </View>

            <View className="flex-row items-baseline gap-2 justify-between">
                <View className="min-w-0 flex-1 flex-row items-baseline gap-4">
                    <View className="border border-secondary/10 bg-secondary/10 rounded-md px-2 shrink-0">
                        <Text
                            className="shrink-0 text-xs text-secondary"
                            testID={
                                TEST_IDS[TEST_IDS.HOME_STUDENT_ROW_NEXT_PICKUP]
                            }
                        >
                            {nextPickup?.date}
                        </Text>
                    </View>
                    <Text
                        variant="muted"
                        numberOfLines={1}
                        className="min-w-0 flex-1"
                        testID={
                            TEST_IDS[
                                TEST_IDS.HOME_STUDENT_ROW_NEXT_PICKUP_PICKER
                            ]
                        }
                    >
                        {nextPickup?.picker}
                    </Text>
                </View>
            </View>
        </View>
    );
}

export function ChildAbsenceSummarySection({
    childList,
    childIdsNeedingInfo,
    pickupRequests,
}: ChildAbsenceSummarySectionProps) {
    const { t } = useTranslation();
    const todayIso = formatIsoDate(new Date());

    const nextPickupByChildId = useMemo(() => {
        const map = new Map<number, { date: string; picker: string }>();

        for (const child of childList) {
            const nearest = getPickupsOnNearestDate(
                pickupRequests.filter(
                    (request) => request.child_id === child.id,
                ),
                todayIso,
            );

            if (nearest) {
                map.set(child.id, {
                    date: formatDisplayDateWithWeekday(nearest.occurrenceDate),
                    picker: nearest.requests[0].picker_name,
                });
            }
        }

        return map;
    }, [childList, pickupRequests, todayIso]);

    if (childList.length === 0) {
        return (
            <View className="flex-1 items-center justify-center">
                <Text>{t("home.noStudents")}</Text>
            </View>
        );
    }

    return (
        <View className="gap-4">
            {childList.map((child) => (
                <ChildSummaryCard
                    key={child.id}
                    child={child}
                    needsInfo={childIdsNeedingInfo.has(child.id)}
                >
                    {nextPickupByChildId.get(child.id) ? (
                        <PickupChangeCard
                            nextPickup={nextPickupByChildId.get(child.id)}
                        />
                    ) : null}
                </ChildSummaryCard>
            ))}
        </View>
    );
}
